import React, { useState, useMemo, useCallback, useEffect } from 'react';
import {
    Autocomplete,
    Box,
    Button,
    Checkbox,
    Chip,
    Collapse,
    FormControlLabel,
    IconButton,
    MenuItem,
    Paper,
    Popper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    TableSortLabel,
    TextField,
    Typography,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import Select from 'react-select';
import Constants from '../../../../assests/Constants/constants';
import {
    loadPaperlessSettings,
    savePaperlessSettings,
    loadPaperlessUsersCache,
    savePaperlessUsersCache,
    matchPaperlessUserByCaseId,
    monthInputToPeriod,
    periodToMonthInput,
    currentMonthInput,
} from './paperlessImportStorage';

// MUI's own .MuiAutocomplete-popper slot sets z-index 1300, which can beat a plain zIndex here
// depending on stylesheet order and leave the list rendering behind the modal body. The doubled
// selector raises specificity so this value always wins, as in BmkvdJournalImportModal.
const StyledPopper = styled(Popper)(({ theme }) => ({
    '&&': {
        zIndex: 9999,
    },
    '& .MuiPaper-root': {
        borderRadius: '8px',
        background: '#FFF',
        boxShadow: '0px 4px 12px 0px rgba(0, 0, 0, 0.15)',
        minWidth: '200px',
    },
    '& .MuiAutocomplete-listbox': {
        padding: theme.spacing(1),
        maxHeight: '280px',
    },
    '& .MuiAutocomplete-option': {
        borderRadius: '4px',
        padding: '8px 12px',
        fontSize: '0.875rem',
        margin: '2px 0',
        '&[aria-selected="true"]': {
            background: 'rgba(48, 79, 255, 0.12) !important',
            fontWeight: 600,
        },
        '&.Mui-focused': {
            background: 'rgba(249, 170, 42, 0.15) !important',
        },
        '&:hover': {
            background: 'rgba(249, 170, 42, 0.25) !important',
        },
    },
}));

// A Select renders its menu in a Popover whose root also defaults to z-index 1300, so it needs the
// same specificity-boosted lift as StyledPopper to stay above the modal body.
const selectMenuProps = {
    MenuProps: {
        sx: { '&&': { zIndex: 9999 } },
    },
};

const palCodeSelectStyles = {
    container: (provided) => ({ ...provided, width: '100%', boxSizing: 'border-box' }),
    control: (provided) => ({
        ...provided,
        minHeight: '40px',
        borderRadius: '8px',
        background: '#F5F5F6',
        border: '1px solid transparent',
        boxShadow: 'none',
        padding: '0 10px',
        textAlign: 'left',
    }),
    placeholder: (provided) => ({ ...provided, textAlign: 'left', color: 'black' }),
    singleValue: (provided) => ({ ...provided, textAlign: 'left', color: 'black' }),
    menu: (provided) => ({ ...provided, textAlign: 'left', color: 'black' }),
    menuPortal: (provided) => ({ ...provided, zIndex: 9999 }),
};

/**
 * Single modal covering the whole Paperless import: one preview and one commit produce both the
 * account cards and the journal entries, because Paperless models a period as one flat document set
 * where each document already carries its counterparty and its sort-code tag.
 */
const PaperlessImportModal = ({
    open,
    onClose,
    previewPaperlessApi,
    commitPaperlessApi,
    fetchPaperlessUsers,
    createMissingSortCodes,
    createMissingPalCode,
    direction,
    t,
    formattedSortCodeVatType = [],
    formattedSortCodesGroupNamesOptions = [],
    palCodeCategoryOptions = [],
    palCodes = [],
    accountCardOptions = [],
    client,
    user,
}) => {
    const storedSettings = useMemo(() => loadPaperlessSettings(), []);

    const [paperlessToken, setPaperlessToken] = useState(storedSettings.token || '');
    const [periodFrom, setPeriodFrom] = useState(
        periodToMonthInput(storedSettings.periodStart) || currentMonthInput()
    );
    const [periodTo, setPeriodTo] = useState(
        periodToMonthInput(storedSettings.periodEnd) || currentMonthInput()
    );
    // 0 = filter by VAT reporting period (תקופת דיווח), 1 = filter by document date (תאריך מסמך).
    const [dateType, setDateType] = useState(storedSettings.dateType ?? 0);
    const [downloadDocuments, setDownloadDocuments] = useState(false);

    const [users, setUsers] = useState(() => loadPaperlessUsersCache());
    const [selectedUser, setSelectedUser] = useState(null);
    const [loadingUsers, setLoadingUsers] = useState(false);

    const [previewDone, setPreviewDone] = useState(false);
    const [processing, setProcessing] = useState(false);

    const [summary, setSummary] = useState({ parsedRows: 0, skippedRows: 0, duplicateCount: 0, newRows: 0 });
    const [accountCards, setAccountCards] = useState([]);
    const [missingPalCodes, setMissingPalCodes] = useState([]);
    const [entries, setEntries] = useState([]);
    const [editableMissing, setEditableMissing] = useState([]);
    const [showMissingSection, setShowMissingSection] = useState(false);
    const [showCardsSection, setShowCardsSection] = useState(false);

    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [orderBy, setOrderBy] = useState('documentDate');
    const [order, setOrder] = useState('asc');
    const [entriesSearch, setEntriesSearch] = useState('');

    const [selectedRows, setSelectedRows] = useState([]);
    const [bulkVatType, setBulkVatType] = useState(null);
    const [bulkGroup, setBulkGroup] = useState(null);
    const [bulkPar, setBulkPar] = useState(null);
    const [bulkVat, setBulkVat] = useState(null);

    const [editingPalCode, setEditingPalCode] = useState(null);
    const [palCodeForm, setPalCodeForm] = useState({ palCodeId: '', palCodeType: '', palCodeCategory: null });

    const vatOptions = useMemo(
        () => (Constants.VATPercentage || []).map((v) => ({ label: `${v}%`, value: v })),
        []
    );
    const parOptions = useMemo(
        () => [{ label: t('Yes'), value: true }, { label: t('No'), value: false }],
        [t]
    );
    const palCodeTypeOptions = useMemo(
        () => (Constants.PalCodeType || []).map((type) => ({ label: type, value: type })),
        []
    );
    const palCodeCategorySelectOptions = useMemo(
        () => (palCodeCategoryOptions || []).map((cat) => ({
            label: cat.palCodeCategoryId,
            value: cat.palCodeCategoryId,
        })),
        [palCodeCategoryOptions]
    );

    const periodStart = monthInputToPeriod(periodFrom);
    const periodEnd = monthInputToPeriod(periodTo);

    // Persist the token and range so the next client opens prefilled.
    useEffect(() => {
        savePaperlessSettings({ token: paperlessToken, periodStart, periodEnd, dateType });
    }, [paperlessToken, periodStart, periodEnd, dateType]);

    useEffect(() => {
        savePaperlessUsersCache(users);
    }, [users]);

    // Auto-select the Paperless business whose VAT number matches this client's caseId.
    useEffect(() => {
        if (!users.length) return;
        const match = matchPaperlessUserByCaseId(users, client?.caseId);
        if (match?.id != null) setSelectedUser(match);
    }, [users, client?.caseId]);

    const effectiveUserId = String(selectedUser?.id ?? '').trim();
    const tokenReady = !!paperlessToken.trim();
    const inputReady = !!(tokenReady && effectiveUserId && periodStart && periodEnd);

    const buildApiParams = useCallback(() => ({
        paperlessToken: paperlessToken.trim(),
        paperlessUserId: effectiveUserId,
        periodStart,
        periodEnd,
        dateType,
        downloadDocuments,
    }), [paperlessToken, effectiveUserId, periodStart, periodEnd, dateType, downloadDocuments]);

    const handleLoadUsers = async () => {
        if (!tokenReady || !fetchPaperlessUsers) return;
        try {
            setLoadingUsers(true);
            const list = await fetchPaperlessUsers({ paperlessToken: paperlessToken.trim() });
            setUsers(Array.isArray(list) ? list : []);
        } catch (err) {
            console.error('Paperless businesses listing failed:', err);
            setUsers([]);
        } finally {
            setLoadingUsers(false);
        }
    };

    const handlePreview = async () => {
        if (!inputReady) return;

        try {
            setProcessing(true);
            setPreviewDone(false);

            const res = await previewPaperlessApi(buildApiParams());

            setSummary({
                parsedRows: res?.parsedRows ?? 0,
                skippedRows: res?.skippedRows ?? 0,
                duplicateCount: res?.duplicateCount ?? 0,
                newRows: res?.newRows ?? 0,
            });
            setAccountCards(res?.accountCards ?? []);
            setMissingPalCodes(res?.missingPalCodes ?? []);
            setEntries(res?.journalEntries ?? []);

            setEditableMissing((res?.missingSortCodes ?? []).map((sc) => ({
                ...sc,
                sortCodeId: sc.sortCodeId || '',
                sortCodeName: sc.sortCodeName || '',
                sortCodeVatType: sc.sortCodeVatType ?? null,
                sortCodeCategory: sc.sortCodeCategory ?? null,
                participatesInProfitAndLoss: sc.participatesInProfitAndLoss ?? null,
                vat: sc.vat ?? 0,
            })));

            setPage(0);
            setOrderBy('documentDate');
            setOrder('asc');
            setEntriesSearch('');
            setSelectedRows([]);
            setShowMissingSection(false);
            setShowCardsSection(false);
            setPreviewDone(true);
        } catch (err) {
            console.error('Paperless preview failed:', err);
            setPreviewDone(false);
        } finally {
            setProcessing(false);
        }
    };

    const handleCreateAndCommit = async () => {
        if (!inputReady) return;

        try {
            setProcessing(true);

            // The sort codes must exist before the commit resolves them; the commit matches on the
            // tag id, so renaming them above does not break the link.
            if (editableMissing.length > 0) {
                await createMissingSortCodes(editableMissing);
            }

            const res = await commitPaperlessApi(buildApiParams());

            const errorCount = res?.errorCount ?? 0;
            const errorMessages = res?.errorMessages ?? [];

            if (errorCount > 0 && errorMessages.length > 0) {
                console.error('Paperless import errors:', errorMessages);
            } else {
                handleClose();
            }
        } catch (err) {
            console.error('Paperless commit error:', err);
        } finally {
            setProcessing(false);
        }
    };

    const calculateNextOrderNumber = (category) => {
        if (!category || !palCodes.length) return 100;
        const inCategory = palCodes.filter((pc) => pc.palCodeCategory === category);
        const maxOrderNumber = inCategory.reduce((max, pc) => Math.max(max, pc.orderNumber || 0), 0);
        return maxOrderNumber > 0 ? maxOrderNumber + 100 : 100;
    };

    const handleCreatePalCode = async (palCodeName) => {
        if (!palCodeForm.palCodeId || !palCodeForm.palCodeType) return;

        try {
            setProcessing(true);
            const response = await createMissingPalCode({
                palCodeId: palCodeForm.palCodeId,
                palCodeType: palCodeForm.palCodeType,
                palCodeCategory: palCodeForm.palCodeCategory,
                orderNumber: calculateNextOrderNumber(palCodeForm.palCodeCategory),
                accountantId: user?.accountantId,
            });

            if (response && response.ok === false) {
                throw new Error(await response.text());
            }

            setMissingPalCodes((prev) => prev.filter((pc) => pc !== palCodeName));
            setEditingPalCode(null);
            setPalCodeForm({ palCodeId: '', palCodeType: '', palCodeCategory: null });
        } catch (err) {
            console.error('PalCode creation error:', err);
        } finally {
            setProcessing(false);
        }
    };

    const updateMissing = (index, field, value) =>
        setEditableMissing((prev) => {
            const arr = [...prev];
            arr[index] = { ...arr[index], [field]: value };
            return arr;
        });

    const applyBulk = () => {
        setEditableMissing((prev) =>
            prev.map((r) =>
                selectedRows.includes(r.sortCodeId)
                    ? {
                        ...r,
                        sortCodeVatType: bulkVatType ? bulkVatType.value : r.sortCodeVatType,
                        sortCodeCategory: bulkGroup ? bulkGroup.value : r.sortCodeCategory,
                        participatesInProfitAndLoss: bulkPar ? bulkPar.value : r.participatesInProfitAndLoss,
                        vat: bulkVat ? bulkVat.value : r.vat,
                    }
                    : r
            )
        );
        setSelectedRows([]);
        setBulkVatType(null);
        setBulkGroup(null);
        setBulkPar(null);
        setBulkVat(null);
    };

    const missingIncomplete = useMemo(
        () => editableMissing.some((sc) =>
            !sc.sortCodeName?.trim() ||
            sc.sortCodeVatType == null ||
            sc.participatesInProfitAndLoss == null ||
            sc.vat == null
        ),
        [editableMissing]
    );

    const formatAmount = (amount) => {
        const num = typeof amount === 'number' ? amount : parseFloat(amount);
        if (amount == null || Number.isNaN(num)) return '';
        return new Intl.NumberFormat('he-IL', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        }).format(num);
    };

    const formatDate = (iso) => {
        if (!iso) return '';
        const d = new Date(iso);
        if (Number.isNaN(d.getTime())) return '';
        const dd = String(d.getDate()).padStart(2, '0');
        const mm = String(d.getMonth() + 1).padStart(2, '0');
        const yy = String(d.getFullYear()).slice(-2);
        return `${dd}/${mm}/${yy}`;
    };

    const getComparator = (ord, key) => (a, b) => {
        const aVal = a[key];
        const bVal = b[key];
        const isNum = (v) => v != null && v !== '' && !Number.isNaN(Number(v));
        if (ord === 'asc') {
            if (isNum(aVal) && isNum(bVal)) return Number(aVal) - Number(bVal);
            return String(aVal ?? '').localeCompare(String(bVal ?? ''), undefined, { numeric: true });
        }
        if (isNum(aVal) && isNum(bVal)) return Number(bVal) - Number(aVal);
        return String(bVal ?? '').localeCompare(String(aVal ?? ''), undefined, { numeric: true });
    };

    // Resolve account card id → display name (for the transactions preview).
    const cardIdToName = useMemo(() => {
        const m = new Map();
        (accountCardOptions || [])
            .filter((o) => o.value1 !== 'AddItem')
            .forEach((o) => {
                const id = o.cardId ?? o.value1 ?? o.value;
                if (id == null || id === '') return;
                const strId = String(id).trim();
                const name = String(o.name ?? o.label ?? o.accountCaseId ?? '').trim() || strId;
                m.set(strId, name);
                if (/^\d+$/.test(strId)) m.set(Number(strId), name);
            });
        return m;
    }, [accountCardOptions]);

    const resolveAccountDisplay = useCallback((id, fallbackName) => {
        const strId = id == null ? '' : String(id).trim();
        const name = (fallbackName && String(fallbackName).trim())
            || (strId && (cardIdToName.get(strId) || cardIdToName.get(Number(strId))))
            || '';
        if (!name && !strId) return '—';
        return name ? (strId ? `${name} (${strId})` : name) : strId;
    }, [cardIdToName]);

    /**
     * Paperless documents are single-sided, so the two journal sides are derived the same way the
     * import does it (PaperlessImportService): an expense debits the sort-code account and credits
     * the counterparty, and an income does the reverse. Flattening them onto the row keeps the
     * search, sort and pagination below working on plain fields, exactly like the Finbot preview.
     */
    const decoratedEntries = useMemo(() => entries.map((e) => {
        const counterparty = resolveAccountDisplay(e.accountCaseId, e.counterpartyName);
        const sortCodeSide = resolveAccountDisplay(e.sortCodeId, e.sortCodeName);
        return {
            ...e,
            transactionType: e.isIncome
                ? (t('Income') || 'הכנסה')
                : (t('Expense') || 'הוצאה'),
            debitAccount: e.isIncome ? counterparty : sortCodeSide,
            creditAccount: e.isIncome ? sortCodeSide : counterparty,
        };
    }), [entries, resolveAccountDisplay, t]);

    const entrySearchHaystack = useCallback((e) => [
        formatDate(e.documentDate),
        e.transactionType,
        e.reference,
        e.debitAccount,
        e.creditAccount,
        e.counterpartyName,
        e.accountCaseId,
        e.sortCodeId,
        e.sortCodeName,
        e.amount,
        e.vatAmount,
        e.description,
        e.confirmationNumber,
    ]
        .filter((v) => v !== null && v !== undefined && v !== '')
        .join(' ')
        .toLowerCase(), []);

    const filteredEntries = useMemo(() => {
        const q = entriesSearch.trim().toLowerCase();
        if (!q) return decoratedEntries;
        return decoratedEntries.filter((e) => entrySearchHaystack(e).includes(q));
    }, [decoratedEntries, entriesSearch, entrySearchHaystack]);

    const sortedEntries = useMemo(() => {
        if (!filteredEntries.length) return [];
        return [...filteredEntries].sort(getComparator(order, orderBy));
    }, [filteredEntries, order, orderBy]);

    const paginatedEntries = useMemo(
        () => sortedEntries.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
        [sortedEntries, page, rowsPerPage]
    );

    const newCardCount = useMemo(
        () => accountCards.filter((c) => !c.alreadyExists).length,
        [accountCards]
    );

    const handleRequestSort = (property) => {
        const isAsc = orderBy === property && order === 'asc';
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(property);
        setPage(0);
    };

    const renderSortHeader = (id, label, align = 'left') => (
        <TableCell align={align} sx={{ fontWeight: 700, fontSize: 12, whiteSpace: 'nowrap' }}>
            <TableSortLabel
                active={orderBy === id}
                direction={orderBy === id ? order : 'asc'}
                onClick={() => handleRequestSort(id)}
            >
                {label}
            </TableSortLabel>
        </TableCell>
    );

    const handleClose = () => {
        // Keep the token / range / loaded businesses so they carry to the next client (they are
        // persisted in localStorage); only the per-import view state is reset here.
        setLoadingUsers(false);
        setDownloadDocuments(false);
        setPreviewDone(false);
        setSummary({ parsedRows: 0, skippedRows: 0, duplicateCount: 0, newRows: 0 });
        setAccountCards([]);
        setMissingPalCodes([]);
        setEntries([]);
        setEditableMissing([]);
        setSelectedRows([]);
        setEditingPalCode(null);
        setPalCodeForm({ palCodeId: '', palCodeType: '', palCodeCategory: null });
        setPage(0);
        setEntriesSearch('');
        setShowMissingSection(false);
        setShowCardsSection(false);
        onClose();
    };

    if (!open) return null;

    return (
        <div className="download-modal-overlay" onClick={handleClose}>
            <div className="download-modal" onClick={(e) => e.stopPropagation()}>
                <div
                    className={`download-modal-content ${direction}`}
                    style={{
                        position: 'relative',
                        width: previewDone ? '95vw' : undefined,
                        maxWidth: previewDone ? '1500px' : '600px',
                    }}
                >
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                        <h4 className="report-title">
                            {t('ImportPaperless') || 'ייבוא מ-Paperless'}
                        </h4>
                        <IconButton className="client-back-icon" onClick={handleClose} sx={{ padding: '4px' }}>
                            <CloseIcon />
                        </IconButton>
                    </Box>

                    <Box display="flex" flexDirection="column" gap={1.5}>
                        <Typography variant="body2" color="text.secondary">
                            {t('PaperlessImportDescription') || 'הייבוא יוצר גם כרטיסי חשבון וגם פקודות יומן.'}
                        </Typography>

                        <TextField
                            label={t('PaperlessToken') || 'Paperless Token'}
                            value={paperlessToken}
                            onChange={(e) => setPaperlessToken(e.target.value)}
                            size="small"
                            fullWidth
                            multiline
                            maxRows={3}
                            placeholder={t('PaperlessTokenPlaceholder') || 'הדבק כאן את ה-Token מ-Paperless'}
                        />

                        {/* Business picker: load and search the accountant's Paperless businesses */}
                        <Box display="flex" gap={1.5} alignItems="center" flexWrap="wrap">
                            <Button
                                variant="outlined"
                                size="small"
                                onClick={handleLoadUsers}
                                disabled={!tokenReady || loadingUsers || processing}
                            >
                                {loadingUsers
                                    ? (t('Processing') || 'Processing')
                                    : (t('LoadPaperlessBusinesses') || 'טען עסקים')}
                            </Button>
                            <Autocomplete
                                options={users}
                                loading={loadingUsers}
                                value={selectedUser}
                                onChange={(e, newValue) => setSelectedUser(newValue)}
                                getOptionLabel={(o) =>
                                    o ? `${o.name || ''}${o.companyId ? ` - ${o.companyId}` : ''}` : ''
                                }
                                isOptionEqualToValue={(a, b) => a?.id === b?.id}
                                noOptionsText={t('NoPaperlessBusinesses') || 'אין עסקים (טען קודם)'}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        placeholder={t('SelectPaperlessBusiness') || 'חפש/בחר עסק'}
                                        size="small"
                                    />
                                )}
                                PopperComponent={StyledPopper}
                                sx={{ flex: 1, minWidth: 240 }}
                            />
                        </Box>

                        <TextField
                            select
                            label={t('PaperlessDateType') || 'סינון לפי'}
                            value={dateType}
                            onChange={(e) => setDateType(Number(e.target.value))}
                            size="small"
                            fullWidth
                            SelectProps={selectMenuProps}
                        >
                            <MenuItem value={0}>{t('ReportingPeriod') || 'תקופת דיווח'}</MenuItem>
                            <MenuItem value={1}>{t('DocumentDate') || 'תאריך מסמך'}</MenuItem>
                        </TextField>

                        <Box display="flex" gap={1.5} flexWrap="wrap">
                            <TextField
                                label={t('FromMonth') || 'מחודש'}
                                type="month"
                                value={periodFrom}
                                onChange={(e) => setPeriodFrom(e.target.value)}
                                size="small"
                                InputLabelProps={{ shrink: true }}
                                sx={{ flex: 1, minWidth: 160 }}
                            />
                            <TextField
                                label={t('ToMonth') || 'עד חודש'}
                                type="month"
                                value={periodTo}
                                onChange={(e) => setPeriodTo(e.target.value)}
                                size="small"
                                InputLabelProps={{ shrink: true }}
                                sx={{ flex: 1, minWidth: 160 }}
                            />
                        </Box>

                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={downloadDocuments}
                                    onChange={(e) => setDownloadDocuments(e.target.checked)}
                                    size="small"
                                />
                            }
                            label={t('ImportWithDocuments') || 'Import with documents'}
                        />

                        <Box display="flex" gap={1.5} mt={1}>
                            <Button
                                variant="contained"
                                onClick={handlePreview}
                                disabled={!inputReady || processing}
                            >
                                {processing && !previewDone ? t('Processing') : t('Preview')}
                            </Button>
                            <Button
                                variant="contained"
                                color="success"
                                disabled={!previewDone || processing || missingIncomplete || missingPalCodes.length > 0}
                                onClick={handleCreateAndCommit}
                            >
                                {processing && previewDone ? t('Processing') : t('CreateAndImport')}
                            </Button>
                        </Box>
                    </Box>

                    {previewDone && (
                        <>
                            <Box mt={2.5} display="flex" gap={1} flexWrap="wrap">
                                <Chip
                                    size="small"
                                    label={`${t('ParsedRows') || 'שורות שנקראו'}: ${summary.parsedRows}`}
                                />
                                <Chip
                                    size="small"
                                    color="success"
                                    label={`${t('NewRowsToImport') || 'שורות חדשות לייבוא'}: ${summary.newRows}`}
                                />
                                <Chip
                                    size="small"
                                    label={`${t('Duplicates') || 'כפילויות'}: ${summary.duplicateCount}`}
                                />
                                <Chip
                                    size="small"
                                    label={`${t('SkippedRows') || 'שורות שדולגו'}: ${summary.skippedRows}`}
                                />
                                <Chip
                                    size="small"
                                    color="primary"
                                    label={`${t('NewAccountCards') || 'כרטיסים חדשים'}: ${newCardCount}`}
                                />
                            </Box>

                            {/* Missing PalCodes — must exist before the cards can be created */}
                            {missingPalCodes.length > 0 && (
                                <Box
                                    mt={2.5}
                                    p={2}
                                    sx={{ backgroundColor: '#fff3cd', borderRadius: 1, border: '1px solid #ffc107' }}
                                >
                                    <Typography variant="subtitle2" sx={{ color: '#856404' }}>
                                        {t('MissingPalCodes') || 'חשבונות ראשיים חסרים'}
                                    </Typography>
                                    <Typography variant="body2" sx={{ color: '#856404', mb: 1.5 }}>
                                        {t('MissingPalCodesDescription') || 'יש ליצור את החשבונות הראשיים הבאים לפני הייבוא:'}
                                    </Typography>

                                    {missingPalCodes.map((palCodeName) => (
                                        <Box
                                            key={palCodeName}
                                            mb={1.5}
                                            p={1.5}
                                            sx={{ backgroundColor: '#fff', borderRadius: 1 }}
                                        >
                                            {editingPalCode === palCodeName ? (
                                                <Box display="flex" flexDirection="column" gap={1.5}>
                                                    <TextField
                                                        label={t('PalCodeId') || 'מזהה חשבון ראשי'}
                                                        value={palCodeForm.palCodeId}
                                                        size="small"
                                                        fullWidth
                                                        disabled
                                                    />
                                                    <Box>
                                                        <Typography variant="body2" fontWeight={500} mb={0.5}>
                                                            {t('PalCodeType') || 'סוג חשבון ראשי'} *
                                                        </Typography>
                                                        <Select
                                                            placeholder={t('Select') || 'בחר'}
                                                            options={palCodeTypeOptions}
                                                            value={palCodeTypeOptions.find((o) => o.value === palCodeForm.palCodeType) || null}
                                                            onChange={(option) =>
                                                                setPalCodeForm({ ...palCodeForm, palCodeType: option?.value || '' })
                                                            }
                                                            styles={palCodeSelectStyles}
                                                            menuPortalTarget={document.body}
                                                            menuPosition="fixed"
                                                        />
                                                    </Box>
                                                    <Box>
                                                        <Typography variant="body2" fontWeight={500} mb={0.5}>
                                                            {t('PalCodeCategory') || 'קטגוריית חשבון ראשי'}
                                                        </Typography>
                                                        <Select
                                                            placeholder={t('None') || 'ללא'}
                                                            options={palCodeCategorySelectOptions}
                                                            value={palCodeCategorySelectOptions.find((o) => o.value === palCodeForm.palCodeCategory) || null}
                                                            onChange={(option) =>
                                                                setPalCodeForm({ ...palCodeForm, palCodeCategory: option?.value || null })
                                                            }
                                                            styles={palCodeSelectStyles}
                                                            menuPortalTarget={document.body}
                                                            menuPosition="fixed"
                                                        />
                                                    </Box>
                                                    <Box display="flex" gap={1.5}>
                                                        <Button
                                                            variant="contained"
                                                            size="small"
                                                            onClick={() => handleCreatePalCode(palCodeName)}
                                                            disabled={processing || !palCodeForm.palCodeType}
                                                        >
                                                            {t('create') || 'צור'}
                                                        </Button>
                                                        <Button
                                                            variant="outlined"
                                                            size="small"
                                                            onClick={() => {
                                                                setEditingPalCode(null);
                                                                setPalCodeForm({ palCodeId: '', palCodeType: '', palCodeCategory: null });
                                                            }}
                                                        >
                                                            {t('Cancel') || 'ביטול'}
                                                        </Button>
                                                    </Box>
                                                </Box>
                                            ) : (
                                                <Box display="flex" justifyContent="space-between" alignItems="center">
                                                    <Typography variant="body2" fontWeight={700}>{palCodeName}</Typography>
                                                    <Button
                                                        variant="outlined"
                                                        size="small"
                                                        onClick={() => {
                                                            setEditingPalCode(palCodeName);
                                                            setPalCodeForm({
                                                                palCodeId: palCodeName,
                                                                palCodeType: '',
                                                                palCodeCategory: null,
                                                            });
                                                        }}
                                                    >
                                                        {t('CreatePalCode') || 'צור חשבון ראשי'}
                                                    </Button>
                                                </Box>
                                            )}
                                        </Box>
                                    ))}
                                </Box>
                            )}

                            {/* Missing sort codes — the tag ids Paperless cannot name for us */}
                            {editableMissing.length > 0 ? (
                                <Box mt={2.5}>
                                    <Box
                                        display="flex"
                                        alignItems="center"
                                        justifyContent="space-between"
                                        sx={{
                                            padding: 2,
                                            backgroundColor: missingIncomplete ? 'action.hover' : 'grey.100',
                                            borderRadius: 1,
                                            border: '1px solid',
                                            borderColor: missingIncomplete ? 'warning.main' : 'divider',
                                        }}
                                    >
                                        <Box>
                                            <Typography variant="body2">
                                                {t('MissingSortCodes')}: <strong>{editableMissing.length}</strong>
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                {t('PaperlessSortCodeIdHint')
                                                    || 'מספר הסוג נשמר כמזהה הקבוע; ניתן לשנות רק את השם.'}
                                            </Typography>
                                        </Box>
                                        <Button
                                            size="small"
                                            variant="outlined"
                                            onClick={() => setShowMissingSection((prev) => !prev)}
                                            endIcon={showMissingSection ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                        >
                                            {showMissingSection ? (t('Hide') || 'Hide') : (t('Open') || 'Open')}
                                        </Button>
                                    </Box>
                                    <Collapse in={showMissingSection}>
                                        <Box mt={2}>
                                            <Box display="flex" flexWrap="wrap" gap={1.5} alignItems="center" mb={2}>
                                                <Autocomplete
                                                    options={formattedSortCodeVatType || []}
                                                    getOptionLabel={(option) => option?.label ?? ''}
                                                    value={bulkVatType}
                                                    onChange={(e, newValue) => setBulkVatType(newValue)}
                                                    isOptionEqualToValue={(a, b) => a?.value === b?.value}
                                                    renderInput={(params) => (
                                                        <TextField {...params} placeholder={t('SelectSortCodeVatType')} size="small" />
                                                    )}
                                                    PopperComponent={StyledPopper}
                                                    sx={{ minWidth: 180 }}
                                                />
                                                <Autocomplete
                                                    options={formattedSortCodesGroupNamesOptions || []}
                                                    getOptionLabel={(option) => option?.label ?? ''}
                                                    value={bulkGroup}
                                                    onChange={(e, newValue) => setBulkGroup(newValue)}
                                                    isOptionEqualToValue={(a, b) => a?.value === b?.value}
                                                    renderInput={(params) => (
                                                        <TextField {...params} placeholder={t('SelectSortCodeGroupName')} size="small" />
                                                    )}
                                                    PopperComponent={StyledPopper}
                                                    sx={{ minWidth: 180 }}
                                                />
                                                <Autocomplete
                                                    options={parOptions}
                                                    getOptionLabel={(option) => option?.label ?? ''}
                                                    value={bulkPar}
                                                    onChange={(e, newValue) => setBulkPar(newValue)}
                                                    isOptionEqualToValue={(a, b) => a?.value === b?.value}
                                                    renderInput={(params) => (
                                                        <TextField {...params} placeholder={t('participatesInProfitAndLoss')} size="small" />
                                                    )}
                                                    PopperComponent={StyledPopper}
                                                    sx={{ minWidth: 140 }}
                                                />
                                                <Autocomplete
                                                    options={vatOptions}
                                                    getOptionLabel={(option) => option?.label ?? ''}
                                                    value={bulkVat}
                                                    onChange={(e, newValue) => setBulkVat(newValue)}
                                                    isOptionEqualToValue={(a, b) => a?.value === b?.value}
                                                    renderInput={(params) => (
                                                        <TextField {...params} placeholder={t('VAT')} size="small" />
                                                    )}
                                                    PopperComponent={StyledPopper}
                                                    sx={{ minWidth: 100 }}
                                                />
                                                <Button
                                                    variant="outlined"
                                                    size="small"
                                                    sx={{ boxSizing: 'border-box' }}
                                                    disabled={!bulkVatType && !bulkGroup && !bulkPar && !bulkVat}
                                                    onClick={applyBulk}
                                                >
                                                    {t('ApplyToSelected')}
                                                </Button>
                                            </Box>

                                            <TableContainer component={Paper} variant="outlined" sx={{ overflowX: 'auto' }}>
                                                <Table size="small" stickyHeader className="sortcode-table editable">
                                                    <TableHead>
                                                        <TableRow>
                                                            <TableCell sx={{ fontWeight: 700, fontSize: 12, width: '5%' }}>
                                                                <input
                                                                    type="checkbox"
                                                                    checked={selectedRows.length === editableMissing.length && editableMissing.length > 0}
                                                                    onChange={(e) =>
                                                                        setSelectedRows(e.target.checked ? editableMissing.map((r) => r.sortCodeId) : [])
                                                                    }
                                                                />
                                                            </TableCell>
                                                            <TableCell sx={{ fontWeight: 700, fontSize: 12 }}>{t('SortCode Id')}</TableCell>
                                                            <TableCell sx={{ fontWeight: 700, fontSize: 12 }}>{t('SortCode Name')}</TableCell>
                                                            <TableCell sx={{ fontWeight: 700, fontSize: 12 }}>{t('UsageCount') || 'מספר שורות'}</TableCell>
                                                            <TableCell sx={{ fontWeight: 700, fontSize: 12 }}>{t('SelectSortCodeVatType')}</TableCell>
                                                            <TableCell sx={{ fontWeight: 700, fontSize: 12 }}>{t('SelectSortCodeGroupName')}</TableCell>
                                                            <TableCell sx={{ fontWeight: 700, fontSize: 12 }}>{t('participatesInProfitAndLoss')}</TableCell>
                                                            <TableCell sx={{ fontWeight: 700, fontSize: 12 }}>{t('VAT')}</TableCell>
                                                        </TableRow>
                                                    </TableHead>
                                                    <TableBody>
                                                        {editableMissing.map((sc, i) => (
                                                            <TableRow key={sc.sortCodeId}>
                                                                <TableCell sx={{ fontSize: 12 }}>
                                                                    <input
                                                                        type="checkbox"
                                                                        checked={selectedRows.includes(sc.sortCodeId)}
                                                                        onChange={(e) =>
                                                                            setSelectedRows((prev) =>
                                                                                e.target.checked
                                                                                    ? [...prev, sc.sortCodeId]
                                                                                    : prev.filter((id) => id !== sc.sortCodeId)
                                                                            )
                                                                        }
                                                                    />
                                                                </TableCell>
                                                                <TableCell sx={{ fontSize: 12 }}>
                                                                    {/* Read-only: the import resolves the sort code by this id, so it
                                                                        must survive the rename in the next column. */}
                                                                    <TextField
                                                                        fullWidth
                                                                        size="small"
                                                                        value={sc.sortCodeId}
                                                                        variant="standard"
                                                                        InputProps={{ readOnly: true, disableUnderline: true }}
                                                                        inputProps={{ style: { textAlign: direction === 'ltr' ? 'left' : 'right' } }}
                                                                    />
                                                                </TableCell>
                                                                <TableCell sx={{ fontSize: 12 }}>
                                                                    <TextField
                                                                        fullWidth
                                                                        size="small"
                                                                        value={sc.sortCodeName || ''}
                                                                        variant="standard"
                                                                        onChange={(e) => updateMissing(i, 'sortCodeName', e.target.value)}
                                                                        placeholder={t('EnterSortCodeName') || 'Enter sortcode name'}
                                                                        error={!sc.sortCodeName?.trim()}
                                                                        inputProps={{ style: { textAlign: direction === 'ltr' ? 'left' : 'right' } }}
                                                                    />
                                                                </TableCell>
                                                                <TableCell sx={{ fontSize: 12 }}>
                                                                    {sc.usageCount ?? ''}
                                                                    {sc.isIncome ? ` (${t('Income') || 'הכנסות'})` : ''}
                                                                </TableCell>
                                                                <TableCell sx={{ fontSize: 12 }}>
                                                                    <Autocomplete
                                                                        options={formattedSortCodeVatType || []}
                                                                        getOptionLabel={(option) => option?.label ?? ''}
                                                                        value={formattedSortCodeVatType.find((opt) => opt.value === sc.sortCodeVatType?.toString()?.trim()) || null}
                                                                        onChange={(e, newValue) => updateMissing(i, 'sortCodeVatType', newValue?.value ?? null)}
                                                                        isOptionEqualToValue={(a, b) => a?.value === b?.value}
                                                                        renderInput={(params) => (
                                                                            <TextField
                                                                                {...params}
                                                                                placeholder={t('SelectSortCodeVatType')}
                                                                                size="small"
                                                                                error={sc.sortCodeVatType == null}
                                                                            />
                                                                        )}
                                                                        PopperComponent={StyledPopper}
                                                                        sx={{ minWidth: 160 }}
                                                                    />
                                                                </TableCell>
                                                                <TableCell sx={{ fontSize: 12 }}>
                                                                    <Autocomplete
                                                                        options={formattedSortCodesGroupNamesOptions || []}
                                                                        getOptionLabel={(option) => option?.label ?? ''}
                                                                        value={formattedSortCodesGroupNamesOptions.find((opt) => opt.value === sc.sortCodeCategory) || null}
                                                                        onChange={(e, newValue) => updateMissing(i, 'sortCodeCategory', newValue?.value ?? null)}
                                                                        isOptionEqualToValue={(a, b) => a?.value === b?.value}
                                                                        renderInput={(params) => (
                                                                            <TextField {...params} placeholder={t('SelectSortCodeGroupName')} size="small" />
                                                                        )}
                                                                        PopperComponent={StyledPopper}
                                                                        sx={{ minWidth: 160 }}
                                                                    />
                                                                </TableCell>
                                                                <TableCell sx={{ fontSize: 12 }}>
                                                                    <Autocomplete
                                                                        options={parOptions}
                                                                        getOptionLabel={(option) => option?.label ?? ''}
                                                                        value={sc.participatesInProfitAndLoss === true
                                                                            ? parOptions[0]
                                                                            : sc.participatesInProfitAndLoss === false ? parOptions[1] : null}
                                                                        onChange={(e, newValue) => updateMissing(i, 'participatesInProfitAndLoss', newValue?.value ?? null)}
                                                                        isOptionEqualToValue={(a, b) => a?.value === b?.value}
                                                                        renderInput={(params) => (
                                                                            <TextField
                                                                                {...params}
                                                                                placeholder={t('participatesInProfitAndLoss')}
                                                                                size="small"
                                                                                error={sc.participatesInProfitAndLoss == null}
                                                                            />
                                                                        )}
                                                                        PopperComponent={StyledPopper}
                                                                        sx={{ minWidth: 120 }}
                                                                    />
                                                                </TableCell>
                                                                <TableCell sx={{ fontSize: 12 }}>
                                                                    <Autocomplete
                                                                        options={vatOptions}
                                                                        getOptionLabel={(option) => option?.label ?? ''}
                                                                        value={vatOptions.find((opt) => opt.value === sc.vat) || null}
                                                                        onChange={(e, newValue) => updateMissing(i, 'vat', newValue?.value ?? null)}
                                                                        isOptionEqualToValue={(a, b) => a?.value === b?.value}
                                                                        renderInput={(params) => (
                                                                            <TextField
                                                                                {...params}
                                                                                placeholder={t('VAT')}
                                                                                size="small"
                                                                                error={sc.vat == null}
                                                                            />
                                                                        )}
                                                                        PopperComponent={StyledPopper}
                                                                        sx={{ minWidth: 100 }}
                                                                    />
                                                                </TableCell>
                                                            </TableRow>
                                                        ))}
                                                    </TableBody>
                                                </Table>
                                            </TableContainer>
                                        </Box>
                                    </Collapse>
                                </Box>
                            ) : (
                                <Box mt={2} p={1.5} bgcolor="success.light" borderRadius={1} border="1px solid" borderColor="success.main">
                                    <Typography variant="body2" color="success.dark">
                                        ✓ {t('NoMissingSortCodes') || 'אין סוגי תנועות חסרים'}
                                    </Typography>
                                </Box>
                            )}

                            {/* Account cards produced by the same import */}
                            {accountCards.length > 0 && (
                                <Box mt={2.5}>
                                    <Box
                                        display="flex"
                                        alignItems="center"
                                        justifyContent="space-between"
                                        sx={{ padding: 2, backgroundColor: 'grey.100', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}
                                    >
                                        <Typography variant="body2">
                                            {t('AccountCards') || 'כרטיסי חשבון'}: <strong>{accountCards.length}</strong>
                                        </Typography>
                                        <Button
                                            size="small"
                                            variant="outlined"
                                            onClick={() => setShowCardsSection((prev) => !prev)}
                                            endIcon={showCardsSection ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                        >
                                            {showCardsSection ? (t('Hide') || 'Hide') : (t('Open') || 'Open')}
                                        </Button>
                                    </Box>
                                    <Collapse in={showCardsSection}>
                                        <TableContainer component={Paper} variant="outlined" sx={{ mt: 2, maxHeight: 360, overflow: 'auto' }}>
                                            <Table size="small" stickyHeader>
                                                <TableHead>
                                                    <TableRow>
                                                        <TableCell sx={{ fontWeight: 700, fontSize: 12 }}>{t('CardName') || 'שם הכרטיס'}</TableCell>
                                                        <TableCell sx={{ fontWeight: 700, fontSize: 12 }}>{t('CardId') || 'מזהה כרטיס'}</TableCell>
                                                        <TableCell sx={{ fontWeight: 700, fontSize: 12 }}>{t('AccountCaseId') || 'מספר עוסק'}</TableCell>
                                                        <TableCell sx={{ fontWeight: 700, fontSize: 12 }}>{t('PalCode') || 'חשבון ראשי'}</TableCell>
                                                        <TableCell sx={{ fontWeight: 700, fontSize: 12 }}>{t('Status') || 'סטטוס'}</TableCell>
                                                    </TableRow>
                                                </TableHead>
                                                <TableBody>
                                                    {accountCards.map((card, i) => {
                                                        const palCodeMissing = missingPalCodes.includes(card.palCode);
                                                        return (
                                                            <TableRow
                                                                key={`${card.cardName}-${i}`}
                                                                hover
                                                                sx={palCodeMissing ? { backgroundColor: '#fff3cd' } : undefined}
                                                            >
                                                                <TableCell sx={{ fontSize: 12 }}>{card.cardName || ''}</TableCell>
                                                                <TableCell sx={{ fontSize: 12 }}>{card.cardId || '—'}</TableCell>
                                                                <TableCell sx={{ fontSize: 12 }}>{card.accountCaseId || ''}</TableCell>
                                                                <TableCell sx={{ fontSize: 12, color: palCodeMissing ? '#856404' : 'inherit' }}>
                                                                    {card.palCode || ''}{palCodeMissing ? ' *' : ''}
                                                                </TableCell>
                                                                <TableCell sx={{ fontSize: 12 }}>
                                                                    {card.alreadyExists
                                                                        ? (t('Existing') || 'קיים')
                                                                        : (t('New') || 'חדש')}
                                                                </TableCell>
                                                            </TableRow>
                                                        );
                                                    })}
                                                </TableBody>
                                            </Table>
                                        </TableContainer>
                                    </Collapse>
                                </Box>
                            )}

                            {/* Journal entries produced by the same import */}
                            {entries.length > 0 && (
                                <Box mt={3} sx={{ maxHeight: 560, overflow: 'auto' }}>
                                    <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1} mb={1}>
                                        <Typography variant="subtitle1" fontWeight={600}>
                                            {t('JournalEntries')} ({filteredEntries.length}{entriesSearch.trim() ? ` / ${entries.length}` : ''})
                                        </Typography>
                                        <TextField
                                            size="small"
                                            placeholder={t('Search')}
                                            value={entriesSearch}
                                            onChange={(ev) => {
                                                setEntriesSearch(ev.target.value);
                                                setPage(0);
                                            }}
                                            InputProps={{
                                                startAdornment: <SearchIcon sx={{ mr: 1, color: 'action.active', fontSize: 20 }} />,
                                            }}
                                            sx={{ minWidth: 220 }}
                                        />
                                    </Box>
                                    <TableContainer component={Paper} variant="outlined" sx={{ overflowX: 'auto' }}>
                                        <Table size="small" stickyHeader>
                                            <TableHead>
                                                <TableRow>
                                                    {renderSortHeader('documentDate', t('DocumentDate'))}
                                                    {renderSortHeader('transactionType', t('TransactionType') || 'סוג תנועה')}
                                                    {renderSortHeader('reference', t('Reference'))}
                                                    {renderSortHeader('debitAccount', t('DebitAccount'))}
                                                    {renderSortHeader('creditAccount', t('CreditAccount'))}
                                                    {renderSortHeader('amount', t('Amount'), 'right')}
                                                    {renderSortHeader('vatAmount', t('VATAmount') || 'סכום מע"מ', 'right')}
                                                    {renderSortHeader('vat', t('VAT'), 'right')}
                                                    {renderSortHeader('withholdingTaxAmount', t('WithholdingTax') || 'ניכוי במקור', 'right')}
                                                    {renderSortHeader('description', t('Description'))}
                                                    <TableCell sx={{ fontWeight: 700, fontSize: 12 }}>{t('Status') || 'סטטוס'}</TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {paginatedEntries.length === 0 ? (
                                                    <TableRow>
                                                        <TableCell colSpan={11} align="center" sx={{ py: 3, color: 'text.secondary' }}>
                                                            {t('NoResults') || t('Search')}
                                                        </TableCell>
                                                    </TableRow>
                                                ) : paginatedEntries.map((e) => (
                                                    <TableRow
                                                        key={e.documentId}
                                                        hover
                                                        sx={e.isDuplicate ? { opacity: 0.55 } : undefined}
                                                    >
                                                        <TableCell sx={{ fontSize: 12, whiteSpace: 'nowrap' }}>{formatDate(e.documentDate)}</TableCell>
                                                        <TableCell sx={{ fontSize: 12, whiteSpace: 'nowrap' }}>{e.transactionType}</TableCell>
                                                        <TableCell sx={{ fontSize: 12 }}>{e.reference || ''}</TableCell>
                                                        <TableCell sx={{ fontSize: 12, maxWidth: 200, whiteSpace: 'normal', wordBreak: 'break-word' }}>
                                                            {e.debitAccount}
                                                        </TableCell>
                                                        <TableCell sx={{ fontSize: 12, maxWidth: 200, whiteSpace: 'normal', wordBreak: 'break-word' }}>
                                                            {e.creditAccount}
                                                        </TableCell>
                                                        <TableCell align="right" sx={{ fontSize: 12 }}>{formatAmount(e.amount)}</TableCell>
                                                        <TableCell align="right" sx={{ fontSize: 12 }}>{formatAmount(e.vatAmount)}</TableCell>
                                                        <TableCell align="right" sx={{ fontSize: 12 }}>
                                                            {e.vat != null ? `${e.vat}%` : ''}
                                                        </TableCell>
                                                        <TableCell align="right" sx={{ fontSize: 12 }}>
                                                            {e.withholdingTaxAmount ? formatAmount(e.withholdingTaxAmount) : ''}
                                                        </TableCell>
                                                        <TableCell sx={{ fontSize: 12, maxWidth: 200, whiteSpace: 'normal', wordBreak: 'break-word' }} title={e.description}>
                                                            {e.description || ''}
                                                        </TableCell>
                                                        <TableCell sx={{ fontSize: 12, whiteSpace: 'nowrap' }}>
                                                            {e.isDuplicate
                                                                ? (t('Duplicate') || 'כפילות')
                                                                : (t('New') || 'חדש')}
                                                        </TableCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                        <TablePagination
                                            component="div"
                                            count={sortedEntries.length}
                                            page={page}
                                            onPageChange={(_, p) => setPage(p)}
                                            rowsPerPage={rowsPerPage}
                                            onRowsPerPageChange={(e) => {
                                                setRowsPerPage(parseInt(e.target.value, 10));
                                                setPage(0);
                                            }}
                                            rowsPerPageOptions={[10, 25, 50, 100]}
                                            labelRowsPerPage={t('RowsPerPage') || 'Rows per page'}
                                        />
                                    </TableContainer>
                                </Box>
                            )}
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

export default PaperlessImportModal;
